"use client";

import { useEffect, useState } from "react";
import {
  fetchQuestions,
  sendQuestionsToAPI,
  updateQuestion,
  deleteQuestion,
} from "../../../services/AdminService";
import { getClient, getClientId } from "../../../services/Service";
import "./SetBotLevels.css";

export default function SetBotLevels() {
  const [clientId, setClientId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creatingChild, setCreatingChild] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [warning, setWarning] = useState("");

  const [selectedParentLevel, setSelectedParentLevel] = useState(null);

  const [showAddChildModal, setShowAddChildModal] = useState(false);
  const [showHierarchyPreview, setShowHierarchyPreview] = useState(false);
  const [modalParent, setModalParent] = useState(null);
  const [newType, setNewType] = useState("text");
  const [newLabel, setNewLabel] = useState("");
  const [newLink, setNewLink] = useState("");
  const [clientSlug, setClientSlug] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showOnlyLeafLevels, setShowOnlyLeafLevels] = useState(false);
  const [copiedPreviewLink, setCopiedPreviewLink] = useState(false);

  const openAddFirstQuestionModal = () => {
    setModalParent({ question_level: 0 });
    setNewType("text");
    setNewLabel("");
    setNewLink("");
    setWarning("");
    setShowAddChildModal(true);
  };

  useEffect(() => {
    const id = getClientId() || sessionStorage.getItem("clientId");
    setClientId(id);

    const slugFromStorage =
      (sessionStorage.getItem("client_slug") || sessionStorage.getItem("clientSlug") || "").trim();
    if (slugFromStorage) {
      setClientSlug(slugFromStorage);
    }

    if (id) {
      loadQuestions(id);
      resolveClientSlug(id, slugFromStorage);
    } else {
      setLoading(false);
      setError("Client ID is missing.");
    }
  }, []);

  const resolveClientSlug = async (id, existingSlug = "") => {
    if (existingSlug) return;
    try {
      const response = await getClient();
      if (response?.status !== "success" || !Array.isArray(response?.data)) return;
      const matched = response.data.find((c) => String(c.id) === String(id));
      const slug = (matched?.client_slug || "").trim();
      if (slug) {
        setClientSlug(slug);
        sessionStorage.setItem("client_slug", slug);
      }
    } catch (slugError) {
      console.error("Unable to resolve client slug:", slugError);
    }
  };

  const extractQuestionsFromResponse = (response) => {
    const collected = [];
    const seenIds = new Set();

    const pushIfQuestion = (item) => {
      if (!item || typeof item !== "object") return;
      if (
        Object.prototype.hasOwnProperty.call(item, "id") &&
        (Object.prototype.hasOwnProperty.call(item, "question_text") ||
          Object.prototype.hasOwnProperty.call(item, "question_level") ||
          Object.prototype.hasOwnProperty.call(item, "question_parent_level"))
      ) {
        const id = item.id;
        if (!seenIds.has(id)) {
          seenIds.add(id);
          collected.push(item);
        }
      }
    };

    const walk = (value) => {
      if (!value) return;
      if (Array.isArray(value)) {
        value.forEach((v) => {
          if (v && typeof v === "object") {
            pushIfQuestion(v);
            Object.values(v).forEach((inner) => walk(inner));
          }
        });
        return;
      }
      if (typeof value === "object") {
        pushIfQuestion(value);
        Object.values(value).forEach((v) => walk(v));
      }
    };

    walk(response);
    return collected;
  };

  const loadQuestions = async (id) => {
    try {
      setLoading(true);
      setError("");
      setWarning("");
      setSuccess("");
      setSelectedParentLevel(null);

      const data = await fetchQuestions(id);
      let list = extractQuestionsFromResponse(data);
      if (!Array.isArray(list)) list = [];

      list.sort((a, b) => (a.question_level || 0) - (b.question_level || 0));
      setQuestions(list);
    } catch (err) {
      console.error("Error loading questions:", err);
      setError(err?.message || "Failed to load options.");
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const getChildrenForParent = (parentLevel) => {
    if (parentLevel === null || parentLevel === undefined) return [];
    return questions
      .filter((q) => q.question_parent_level === parentLevel)
      .sort((a, b) => (a.question_level || 0) - (b.question_level || 0));
  };

  const getTypeMeta = (q) => {
    const t = Number(q.question_type || 1);
    switch (t) {
      case 2:
        return { icon: "FILE", label: "File" };
      case 3:
        return { icon: "IMG", label: "Image" };
      case 4:
        return { icon: "VID", label: "Video" };
      case 5:
        return { icon: "REDIR", label: "Redirect" };
      case 6:
        return { icon: "LINK", label: "Link" };
      default:
        return { icon: "TXT", label: "Text" };
    }
  };

  const wouldCreateCycle = (childLevel, newParentLevel) => {
    if (childLevel == null || newParentLevel == null) return false;

    const levelToParent = new Map();
    questions.forEach((q) => {
      if (q.question_level == null) return;
      const lvl = Number(q.question_level);
      let parent = q.question_parent_level;
      if (parent === undefined) parent = null;
      levelToParent.set(lvl, parent);
    });

    levelToParent.set(Number(childLevel), Number(newParentLevel));

    let current = Number(newParentLevel);
    const target = Number(childLevel);
    let safety = 0;

    while (current != null && safety < 1000) {
      if (current === target) return true;
      const next = levelToParent.get(current);
      if (next === undefined || next === null) break;
      current = Number(next);
      safety += 1;
    }

    return false;
  };

  const handleSelectParent = (level) => {
    setWarning("");
    setSelectedParentLevel(level);
  };

  const handleClearParentSelection = () => {
    setSelectedParentLevel(null);
  };

  const handleAddAsChild = (childQuestion) => {
    if (selectedParentLevel == null) {
      setWarning("Please mark a main option before linking another under it.");
      return;
    }

    const childLevel = childQuestion.question_level;
    const parentLevel = selectedParentLevel;

    if (childLevel == null) {
      setWarning("Selected option has no level defined; cannot link.");
      return;
    }

    if (childQuestion.question_parent_level === parentLevel) {
      setWarning(
        `"${childQuestion.question_text || "Option"}" is already linked under level ${parentLevel}.`
      );
      return;
    }

    if (Number(childLevel) === Number(parentLevel)) {
      setWarning(`Level ${childLevel} cannot be its own main option.`);
      return;
    }

    if (wouldCreateCycle(childLevel, parentLevel)) {
      setWarning(
        `Linking "${childQuestion.question_text || "Option"}" under level ${parentLevel} would create a loop.`
      );
      return;
    }

    setQuestions((prev) =>
      prev.map((q) =>
        q.id === childQuestion.id
          ? { ...q, question_parent_level: parentLevel }
          : q
      )
    );

    setWarning("");
  };

  const openAddChildModal = (parentRow) => {
    setModalParent(parentRow);
    setNewType("text");
    setNewLabel("");
    setNewLink("");
    setWarning("");
    setShowAddChildModal(true);
  };

  const closeAddChildModal = () => {
    if (creatingChild) return;
    setShowAddChildModal(false);
    setModalParent(null);
  };

  const getQuestionTypeFromLink = (link) => {
    const videoExtensions = [".mp4", ".avi", ".mov"];
    const imageExtensions = [".jpg", ".jpeg", ".png", ".gif"];
    const fileExtensions = [".pdf", ".docx"];
    const domainExtensions = [".com", ".in", ".org", ".net", ".html", ".js"];
    const lowerCaseLink = link.toLowerCase();

    if (videoExtensions.some((ext) => lowerCaseLink.endsWith(ext))) return 4;
    if (imageExtensions.some((ext) => lowerCaseLink.endsWith(ext))) return 3;
    if (fileExtensions.some((ext) => lowerCaseLink.endsWith(ext))) return 2;
    if (domainExtensions.some((ext) => lowerCaseLink.endsWith(ext))) return 5;
    return 6;
  };

  const handleSubmitNewChild = async (e) => {
    e.preventDefault();
    if (!modalParent) return;

    if (!clientId) {
      setWarning("Client ID is missing.");
      return;
    }

    if ((newType === "text" || newType === "other") && !newLabel.trim()) {
      setWarning("Please enter option text.");
      return;
    }

    if (newType === "link" && (!newLabel.trim() || !newLink.trim())) {
      setWarning("Please enter both label and URL for the link option.");
      return;
    }

    let p_question_type = 1;
    let p_question_label = "";

    if (newType === "link") {
      p_question_type = getQuestionTypeFromLink(newLink);
      p_question_label = newLink.trim();
    } else if (newType === "other") {
      p_question_label = newLabel.trim();
    }

    const numericClientId = Number(clientId) || 1;

    const questionData = {
      action_type: "I",
      p_question_text: newLabel.trim(),
      p_question_label,
      p_question_type,
      p_client_id: numericClientId,
      p_question_level: 1,
      p_question_parent_level: modalParent.question_level,
    };

    try {
      setCreatingChild(true);
      setWarning("");
      setError("");

      await sendQuestionsToAPI(questionData);

      setShowAddChildModal(false);
      setModalParent(null);
      setNewLabel("");
      setNewLink("");

      await loadQuestions(numericClientId);
      setSuccess("Sub-option added successfully.");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error adding sub-option:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to add sub-option."
      );
    } finally {
      setCreatingChild(false);
    }
  };

  const handleRemoveOption = async (row) => {
    if (!clientId) return;

    const hasChildren = questions.some(
      (q) => q.question_parent_level === row.question_level
    );
    if (hasChildren) {
      setWarning(
        `Cannot remove "${row.question_text}". It still has linked sub-options.`
      );
      return;
    }

    const confirmed = window.confirm(
      `Remove option "${row.question_text}"? This cannot be undone.`
    );
    if (!confirmed) return;

    try {
      setSaving(true);
      setError("");
      setWarning("");

      await deleteQuestion({
        p_id: row.id,
        p_question_text: row.question_text || "No Text Available",
        p_question_label: row.question_label || null,
        p_question_type: row.question_type || 1,
        p_client_id: Number(clientId),
        p_question_parent_level: row.question_parent_level ?? null,
      });

      await loadQuestions(clientId);
      setSuccess("Option removed successfully.");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error removing option:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to remove option."
      );
    } finally {
      setSaving(false);
    }
  };

  const validateHierarchyBeforeSave = () => {
    const rootChildren = questions.filter((q) => q.question_parent_level === 0);
    if (rootChildren.length === 0 && questions.length > 0) {
      return "Level 0 should have at least one top-level option.";
    }

    for (const child of questions) {
      const pl = child.question_parent_level;
      if (pl == null || pl === undefined) {
        return `Option "${child.question_text || child.id}" has no parent level assigned.`;
      }
      if (pl === 0) continue;

      const parent = questions.find((q) => q.question_level === pl);
      if (!parent) {
        return `Option "${child.question_text || child.id}" has no valid main option for level ${pl}.`;
      }
    }

    for (const child of questions) {
      if (
        child.question_level != null &&
        child.question_parent_level != null &&
        child.question_parent_level !== 0 &&
        wouldCreateCycle(child.question_level, child.question_parent_level)
      ) {
        return `Circular reference detected involving level ${child.question_level}.`;
      }
    }

    return null;
  };

  const handleSaveChanges = async () => {
    if (!clientId) return;

    setError("");
    setWarning("");
    setSuccess("");

    const validationError = validateHierarchyBeforeSave();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSaving(true);

      const numericClientId = Number(clientId) || 1;
      const payload = {
        questions: questions.map((q) => ({
          action_type: "U",
          p_id: q.id,
          p_question_text: q.question_text,
          p_question_label: q.question_label,
          p_question_type: q.question_type || 1,
          p_client_id: numericClientId,
          p_question_level: q.question_level,
          p_question_parent_level:
            q.question_parent_level === 0 ? null : q.question_parent_level,
        })),
      };

      await updateQuestion(payload);
      setSuccess("Hierarchy saved successfully.");
      await loadQuestions(numericClientId);
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      console.error("Error saving hierarchy:", err);
      setError(err?.message || "Failed to save changes.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center p-5">
        <div className="spinner-border me-3" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <span>Loading bot options...</span>
      </div>
    );
  }

  const rows = [...questions].sort(
    (a, b) => (a.question_level || 0) - (b.question_level || 0)
  );
  const rootChildren = getChildrenForParent(0);
  const linkedRows = questions.filter(
    (q) => q.question_parent_level !== null && q.question_parent_level !== undefined
  ).length;

  const uniqueLevels = Array.from(
    new Set(
      questions
        .map((q) => q.question_level)
        .filter((lv) => lv !== null && lv !== undefined)
    )
  ).sort((a, b) => a - b);
  const chatbotPreviewUrl = clientSlug
    ? `https://ataichatbot.ataibot.in/chatbot/${clientSlug}`
    : "";
  const normalizedSearch = searchTerm.trim().toLowerCase();

  const matchesSearch = (question) => {
    if (!normalizedSearch) return true;
    const searchable = [
      question?.question_text,
      question?.question_label,
      String(question?.question_level ?? ""),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return searchable.includes(normalizedSearch);
  };

  const displayedRows = rows.filter((row) => {
    if (showOnlyLeafLevels && getChildrenForParent(row.question_level).length > 0) return false;
    if (!normalizedSearch) return true;
    if (matchesSearch(row)) return true;
    const children = getChildrenForParent(row.question_level);
    return children.some((child) => matchesSearch(child));
  });

  const displayedRootChildren = rootChildren.filter((child) => matchesSearch(child));
  const hasDisplayedLevel0Row = displayedRows.some((r) => r.question_level === 0);

  const handleCopyPreviewUrl = async () => {
    if (!chatbotPreviewUrl || !navigator?.clipboard) return;
    try {
      await navigator.clipboard.writeText(chatbotPreviewUrl);
      setCopiedPreviewLink(true);
      window.setTimeout(() => setCopiedPreviewLink(false), 1600);
    } catch (copyError) {
      console.error("Failed to copy chatbot URL:", copyError);
    }
  };

  const getPreviewChildrenForParent = (parentLevel) =>
    questions
      .filter((q) => Number(q.question_parent_level) === Number(parentLevel))
      .sort((a, b) => (a.question_level || 0) - (b.question_level || 0));

  const renderHierarchyBranch = (parentLevel, lineage = new Set()) => {
    const children = getPreviewChildrenForParent(parentLevel);
    if (!children.length) return null;

    return (
      <ul className="bot-levels-tree-list">
        {children.map((child) => {
          const level = Number(child.question_level);
          const isLoop = lineage.has(level);
          const typeMeta = getTypeMeta(child);
          const nextLineage = new Set(lineage);
          nextLineage.add(level);

          return (
            <li key={`preview-${child.id}-${parentLevel}`} className="bot-levels-tree-item">
              <div className="bot-levels-tree-node">
                <span className="bot-levels-tree-level">L{child.question_level ?? "-"}</span>
                <span className="bot-levels-tree-text">{child.question_text || "Untitled option"}</span>
                <span className="bot-levels-tree-type">{typeMeta.icon}</span>
              </div>
              {isLoop ? (
                <div className="bot-levels-tree-loop">Loop detected at level {level}</div>
              ) : (
                renderHierarchyBranch(level, nextLineage)
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className="container-fluid mt-3 bot-levels-page">
      <div className="bot-levels-hero mb-3">
        <div>
          <div className="bot-levels-kicker">Conversation Builder</div>
          <h3 className="mb-1">Set Bot Levels</h3>
          <p className="mb-0 text-muted">
            Configure structured parent-child option flow for your Leadbot.
          </p>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {questions.length === 0 ? (
            <button
              className="btn btn-outline-primary btn-sm rounded-pill px-3"
              onClick={openAddFirstQuestionModal}
            >
              Add Root Option
            </button>
          ) : null}
          <button
            className="btn btn-outline-dark btn-sm rounded-pill px-3"
            disabled={questions.length === 0}
            onClick={() => setShowHierarchyPreview(true)}
          >
            Preview
          </button>
          <button
            className="btn btn-success btn-sm rounded-pill px-3"
            disabled={saving}
            onClick={handleSaveChanges}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      <div className="bot-levels-preview-bar mb-3">
        <span className="bot-levels-preview-label">Chatbot Live URL:</span>
        {chatbotPreviewUrl ? (
          <>
            <a
              href={chatbotPreviewUrl}
              target="_blank"
              rel="noreferrer"
              className="bot-levels-preview-link"
              title="Open chatbot preview in a new tab"
            >
              {chatbotPreviewUrl}
            </a>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm rounded-pill px-2 py-1"
              onClick={handleCopyPreviewUrl}
            >
              {copiedPreviewLink ? "Copied" : "Copy"}
            </button>
          </>
        ) : (
          <span className="text-muted small">
            Chatbot URL will appear after client slug is available.
          </span>
        )}
      </div>

      {success && (
        <div className="alert alert-success alert-dismissible fade show bot-levels-alert" role="alert">
          {success}
          <button type="button" className="btn-close" onClick={() => setSuccess("")} />
        </div>
      )}

      {warning && (
        <div className="alert alert-warning alert-dismissible fade show bot-levels-alert" role="alert">
          {warning}
          <button type="button" className="btn-close" onClick={() => setWarning("")} />
        </div>
      )}

      {error && (
        <div className="alert alert-danger alert-dismissible fade show bot-levels-alert" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError("")} />
        </div>
      )}

      <div className="bot-levels-tip mb-3 d-flex align-items-start gap-2">
        <span className="bot-levels-tip-label">Workflow</span>
        <div className="small bot-levels-workflow-steps">
          <span><strong>Step 1:</strong> Select a parent using <strong>Main</strong>.</span>
          <span><strong>Step 2:</strong> Use <strong>Link</strong> to attach existing levels under it.</span>
          <span><strong>Step 3:</strong> Use <strong>Add</strong> to create a new sub-option directly under that row.</span>
          <span className="text-muted">Drag-drop is intentionally disabled to keep level/parent mapping safe.</span>
        </div>
      </div>

      <div className="row g-2 mb-3">
        <div className="col-md-4">
          <div className="bot-levels-metric">
            <div className="bot-levels-metric-label">Total Options</div>
            <div className="bot-levels-metric-value">{rows.length}</div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="bot-levels-metric">
            <div className="bot-levels-metric-label">Root Options</div>
            <div className="bot-levels-metric-value">{rootChildren.length}</div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="bot-levels-metric">
            <div className="bot-levels-metric-label">Linked Nodes</div>
            <div className="bot-levels-metric-value">{linkedRows}</div>
          </div>
        </div>
      </div>

      <div className="mb-3 d-flex align-items-center flex-wrap gap-1">
        <span className="small text-muted me-2">Available levels:</span>
        <span className="badge bg-dark me-1">0</span>
        {uniqueLevels
          .filter((lv) => lv !== 0)
          .map((lv) => (
            <span key={lv} className="badge bg-secondary me-1">
              {lv}
            </span>
          ))}
      </div>

      <div className="card bot-levels-card">
        <div className="card-body">
          {rows.length === 0 ? (
            <div className="text-center py-5">
              <h5 className="mb-2">No bot questions found</h5>
              <p className="text-muted mb-4">
                Start by adding your first question. This becomes the entry point for the chatbot.
              </p>
              <button className="btn btn-primary rounded-pill px-4" onClick={openAddFirstQuestionModal}>
                Add First Question
              </button>
              <div className="mt-3 small text-muted">
                Tip: this question appears as the first prompt for visitors.
              </div>
            </div>
          ) : (
            <>
              <div className="bot-levels-table-header mb-2">
                <div>
                  <div className="bot-levels-table-title">Hierarchy Table</div>
                  <div className="bot-levels-table-subtitle">
                    Build flow by selecting a main level first, then link or add child options.
                  </div>
                </div>
                <div className="bot-levels-table-legend">
                  <span className="badge text-bg-success">Main Selected</span>
                  <span className="badge text-bg-light">Safe Remove: no child nodes</span>
                </div>
              </div>

              <div className="bot-levels-toolbar mb-2">
                <div className="bot-levels-toolbar-left">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search by level, message, or label"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <button
                    type="button"
                    className={`btn btn-sm rounded-pill px-3 ${showOnlyLeafLevels ? "btn-dark" : "btn-outline-dark"}`}
                    onClick={() => setShowOnlyLeafLevels((prev) => !prev)}
                  >
                    {showOnlyLeafLevels ? "Leaf Only: On" : "Leaf Only"}
                  </button>
                  {(searchTerm || showOnlyLeafLevels) ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                      onClick={() => {
                        setSearchTerm("");
                        setShowOnlyLeafLevels(false);
                      }}
                    >
                      Clear
                    </button>
                  ) : null}
                </div>
                <div className="bot-levels-toolbar-right">
                  Showing {displayedRows.length} of {rows.length} levels
                </div>
              </div>

              {selectedParentLevel != null ? (
                <div className="bot-levels-selection-banner mb-2">
                  <span className="badge text-bg-success">Main Level {selectedParentLevel} Selected</span>
                  <span className="small text-muted">
                    Click <strong>Link</strong> on any row to attach it as a child.
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                    onClick={handleClearParentSelection}
                  >
                    Clear Main
                  </button>
                </div>
              ) : null}

              <div className="table-responsive">
                <table className="table table-bordered table-hover align-middle bot-levels-table">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "90px" }}>Level</th>
                      <th>Message</th>
                      <th style={{ width: "42%" }}>Linked Sub-Options</th>
                      <th style={{ width: "220px" }} className="bot-levels-actions-cell">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!hasDisplayedLevel0Row && (
                      <tr className="bot-levels-row-virtual">
                        <td>
                          <span className="bot-levels-level-pill">0</span>
                        </td>
                        <td>
                          <div className="text-muted small fst-italic">
                            Level 0 (top-level options with no main option)
                          </div>
                        </td>
                        <td>
                          {displayedRootChildren.length === 0 ? (
                            <span className="text-muted small bot-levels-empty-note">
                              Top-level options will appear here.
                            </span>
                          ) : (
                            <div className="d-flex flex-wrap gap-2">
                              {displayedRootChildren.map((child) => {
                                const meta = getTypeMeta(child);
                                return (
                                  <span
                                    key={child.id}
                                    className="badge rounded-pill bot-levels-chip d-inline-flex align-items-center gap-2 px-3 py-2"
                                    title={`${meta.label} option`}
                                  >
                                    <span className="bot-levels-chip-type">{meta.icon}</span>
                                    <span className="bot-levels-chip-text">
                                      {child.question_text || "Untitled"}
                                    </span>
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </td>
                        <td className="text-muted small">Root option set</td>
                      </tr>
                    )}

                    {displayedRows.length === 0 ? (
                      <tr>
                        <td colSpan="4" className="text-center text-muted py-4">
                          No levels match your current filters.
                        </td>
                      </tr>
                    ) : displayedRows.map((row) => {
                      const children = getChildrenForParent(row.question_level);
                      const isSelectedParent =
                        selectedParentLevel != null &&
                        row.question_level === selectedParentLevel;
                      const hasChildren = children.length > 0;
                      const canRemove = !hasChildren;

                      return (
                        <tr key={row.id} className={isSelectedParent ? "bot-levels-row-selected" : ""}>
                          <td>
                            <span className="bot-levels-level-pill">
                              {row.question_level ?? "-"}
                            </span>
                          </td>
                          <td>
                            {row.question_text ? (
                              <strong>{row.question_text}</strong>
                            ) : (
                              <em className="text-muted">-</em>
                            )}
                          </td>
                          <td>
                            {children.length === 0 ? (
                              <span className="text-muted small bot-levels-empty-note">
                                No sub-options yet. Use <strong>Add Sub-Option</strong> or link an existing row.
                              </span>
                            ) : (
                              <div className="d-flex flex-wrap gap-2">
                                {children.map((child) => {
                                  const meta = getTypeMeta(child);
                                  return (
                                    <span
                                      key={child.id}
                                      className="badge rounded-pill bot-levels-chip"
                                      title={`${meta.label} option`}
                                    >
                                      <span className="bot-levels-chip-type">{meta.icon}</span>
                                      <span className="bot-levels-chip-text">{child.question_text || "Untitled"}</span>
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </td>
                          <td className="bot-levels-actions-cell">
                            <div className="bot-levels-action-stack">
                              <div className="d-flex flex-wrap gap-2 justify-content-end">
                                <button
                                  type="button"
                                  className="btn btn-success btn-sm bot-levels-btn"
                                  onClick={() => openAddChildModal(row)}
                                  title="Add sub-option under this row"
                                >
                                  Add
                                </button>

                                {isSelectedParent ? (
                                  <>
                                    <span className="badge bg-success bot-levels-badge-selected align-self-center">
                                      Main Selected
                                    </span>
                                    <button
                                      type="button"
                                      className="btn btn-outline-secondary btn-sm bot-levels-btn"
                                      onClick={handleClearParentSelection}
                                      title="Clear selected main option"
                                    >
                                      Clear
                                    </button>
                                  </>
                                ) : selectedParentLevel == null ? (
                                  <button
                                    type="button"
                                    className="btn btn-primary btn-sm bot-levels-btn"
                                    onClick={() => handleSelectParent(row.question_level)}
                                    title="Mark as main option"
                                  >
                                    Main
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="btn btn-outline-primary btn-sm bot-levels-btn"
                                    onClick={() => handleAddAsChild(row)}
                                    title="Link this option under selected main"
                                  >
                                    Link
                                  </button>
                                )}

                                {canRemove && (
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm bot-levels-btn"
                                    onClick={() => handleRemoveOption(row)}
                                    disabled={saving}
                                    title="Remove this option"
                                  >
                                    Remove
                                  </button>
                                )}
                              </div>
                              <div className="bot-levels-action-note">
                                {isSelectedParent
                                  ? "This level is currently selected as parent."
                                  : selectedParentLevel == null
                                    ? "Choose Main to start linking flow."
                                    : "Use Link to attach this level to selected parent."}
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mt-3 d-flex justify-content-center gap-2">
                <button
                  className="btn btn-outline-secondary rounded-pill px-3"
                  disabled={loading || saving}
                  onClick={() => clientId && loadQuestions(clientId)}
                >
                  Reset
                </button>
                <button
                  className="btn btn-success rounded-pill px-3"
                  disabled={saving}
                  onClick={handleSaveChanges}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {showAddChildModal && modalParent && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(15,23,42,0.42)" }}
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content bot-levels-modal">
              <div className="modal-header">
                <h5 className="modal-title">
                  Add Level Option (Level {modalParent.question_level})
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeAddChildModal}
                  aria-label="Close"
                />
              </div>
              <form onSubmit={handleSubmitNewChild}>
                <div className="modal-body">
                  <p className="small text-muted mb-3">
                    Define what visitors can select at this point in the conversation.
                  </p>

                  <div className="mb-3">
                    <div className="btn-group bot-levels-type-toggle" role="group">
                      <button
                        type="button"
                        className={`btn ${newType === "text" ? "btn-dark" : "btn-outline-secondary"}`}
                        onClick={() => setNewType("text")}
                      >
                        Quick Reply (Text)
                      </button>
                      <button
                        type="button"
                        className={`btn ${newType === "link" ? "btn-primary" : "btn-outline-secondary"}`}
                        onClick={() => setNewType("link")}
                      >
                        Content Link (URL)
                      </button>
                    </div>
                  </div>

                  {newType === "text" && (
                    <div className="mb-3">
                      <label className="form-label small text-muted">Text option</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter text option to assist your visitor"
                        value={newLabel}
                        onChange={(e) => setNewLabel(e.target.value)}
                      />
                    </div>
                  )}

                  {newType === "link" && (
                    <div className="mb-3">
                      <label className="form-label small text-muted">Link details</label>
                      <div className="d-flex flex-row gap-2 bot-levels-modal-link-grid">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Link text"
                          value={newLabel}
                          onChange={(e) => setNewLabel(e.target.value)}
                        />
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter URL (website, PDF, video)"
                          value={newLink}
                          onChange={(e) => setNewLink(e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary rounded-pill px-3"
                    onClick={closeAddChildModal}
                    disabled={creatingChild}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary rounded-pill px-3"
                    disabled={creatingChild}
                  >
                    {creatingChild ? "Saving..." : "Add Level Option"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showHierarchyPreview && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(15,23,42,0.5)" }}
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog modal-xl" role="document">
            <div className="modal-content bot-levels-modal bot-levels-preview-modal">
              <div className="modal-header">
                <h5 className="modal-title">Hierarchy Preview</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowHierarchyPreview(false)}
                  aria-label="Close"
                />
              </div>
              <div className="modal-body">
                <div className="bot-levels-preview-summary">
                  <span className="badge text-bg-primary">ROOT (L0)</span>
                  <span className="text-muted small">
                    Read-only tree view of current mapping. Save changes in table if updates are needed.
                  </span>
                </div>
                {questions.length === 0 ? (
                  <div className="text-muted">No hierarchy to preview yet.</div>
                ) : (
                  <div className="bot-levels-tree-wrap">
                    {renderHierarchyBranch(0)}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary rounded-pill px-3"
                  onClick={() => setShowHierarchyPreview(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
